import type { Edge, Node } from '@xyflow/react';

const ARS_ORDER = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '#'];
const CONDITION_ORDER = ['true', 'false'];
const NODE_GAP = 80;
const ROW_GAP = 150;
const COMPONENT_GAP = 220;

const sizeOf = (node: Node) => ({
  width: node.measured?.width || node.width || 260,
  height: Math.max(160, node.measured?.height || node.height || 0),
});

const handleOrder = (type: unknown, handle: string | null | undefined) => {
  const order = type === 'ARS' ? ARS_ORDER : typeof type === 'string' && type.startsWith('CONDITION_') ? CONDITION_ORDER : [];
  const index = order.indexOf(handle || '');
  return index < 0 ? order.length ? 999 : 0 : index;
};

export const layoutIvrGraph = (nodes: Node[], edges: Edge[]): Record<string, { x: number; y: number }> => {
  if (!nodes.length) return {};
  const byId = new Map(nodes.map(node => [node.id, node]));
  const outgoing = new Map(nodes.map(node => [node.id, [] as Edge[]]));
  const incoming = new Map(nodes.map(node => [node.id, [] as Edge[]]));
  edges.forEach(edge => {
    if (!byId.has(edge.source) || !byId.has(edge.target)) throw new Error('없는 노드를 참조하는 연결이 있어 자동 정리할 수 없습니다.');
    outgoing.get(edge.source)!.push(edge);
    incoming.get(edge.target)!.push(edge);
  });
  const indegree = new Map(nodes.map(node => [node.id, 0]));
  const depth = new Map(nodes.map(node => [node.id, 0]));
  outgoing.forEach(sourceEdges => sourceEdges.forEach(edge => indegree.set(edge.target, indegree.get(edge.target)! + 1)));
  const queue = nodes.filter(node => indegree.get(node.id) === 0).map(node => node.id);
  for (let index = 0; index < queue.length; index += 1) {
    const source = queue[index];
    outgoing.get(source)!.forEach(edge => {
      depth.set(edge.target, Math.max(depth.get(edge.target)!, depth.get(source)! + 1));
      const remaining = indegree.get(edge.target)! - 1;
      indegree.set(edge.target, remaining);
      if (remaining === 0) queue.push(edge.target);
    });
  }
  if (queue.length !== nodes.length) throw new Error('순환 연결이 있어 자동 정리할 수 없습니다.');

  const orderedChildren = (id: string) => {
    const ranks = new Map<string, number>();
    outgoing.get(id)!.forEach(edge => {
      const type = byId.get(id)!.data.ivr_type;
      let legacyHandle: string | undefined;
      if (!edge.sourceHandle && typeof edge.label === 'string') {
        if (type === 'ARS') legacyHandle = edge.label.match(/^번호:\s*([0-9*#])(?=\s|$|·)/)?.[1];
        else if (typeof type === 'string' && type.startsWith('CONDITION_')) {
          legacyHandle = edge.label.trim() === '참' ? 'true' : edge.label.trim() === '그 외' ? 'false' : undefined;
        }
      }
      const rank = handleOrder(type, edge.sourceHandle || legacyHandle);
      ranks.set(edge.target, Math.min(ranks.get(edge.target) ?? Infinity, rank));
    });
    return Array.from(ranks.keys()).sort((a, b) =>
      ranks.get(a)! - ranks.get(b)! || byId.get(a)!.position.x - byId.get(b)!.position.x || a.localeCompare(b)
    );
  };

  const primaryParent = new Map<string, string>();
  nodes.forEach(node => {
    // A shared node belongs to the deepest incoming branch; other edges remain untouched.
    const best = [...incoming.get(node.id)!].sort((a, b) => depth.get(b.source)! - depth.get(a.source)! || a.source.localeCompare(b.source))[0];
    if (best) primaryParent.set(node.id, best.source);
  });
  const children = new Map(nodes.map(node => [node.id, orderedChildren(node.id).filter(child => primaryParent.get(child) === node.id)]));
  const start = nodes.find(node => node.data.ivr_type === 'START');
  const roots = nodes.filter(node => !primaryParent.has(node.id))
    .sort((a, b) => Number(b.id === start?.id) - Number(a.id === start?.id) || a.position.x - b.position.x || a.id.localeCompare(b.id))
    .map(node => node.id);

  const widths = new Map<string, number>();
  const subtreeWidth = (id: string): number => {
    // Reserve enough horizontal space for every ordered child subtree.
    const own = sizeOf(byId.get(id)!).width;
    const childIds = children.get(id)!;
    const childWidth = childIds.reduce((sum, child) => sum + subtreeWidth(child), 0) + Math.max(0, childIds.length - 1) * NODE_GAP;
    const width = Math.max(own, childWidth);
    widths.set(id, width);
    return width;
  };
  const leafCenters = new Map<string, number>();
  const placeLeaves = (id: string, left: number) => {
    const childIds = children.get(id)!;
    if (!childIds.length) { leafCenters.set(id, left + widths.get(id)! / 2); return; }
    let cursor = left + (widths.get(id)! - childIds.reduce((sum, child) => sum + widths.get(child)!, 0) - (childIds.length - 1) * NODE_GAP) / 2;
    childIds.forEach(child => { placeLeaves(child, cursor); cursor += widths.get(child)! + NODE_GAP; });
  };
  const centerOf = (id: string, anchor: number, centers: Map<string, number>): number => {
    const childIds = children.get(id)!;
    if (!childIds.length) return leafCenters.get(id)!;
    const childCenters = childIds.map(child => centerOf(child, anchor, centers));
    // Keep each parent on the child lane nearest the central spine.
    const center = Math.max(childCenters[0], Math.min(anchor, childCenters[childCenters.length - 1]));
    centers.set(id, center);
    return center;
  };

  const rowHeights = new Map<number, number>();
  nodes.forEach(node => rowHeights.set(depth.get(node.id)!, Math.max(rowHeights.get(depth.get(node.id)!) || 0, sizeOf(node).height)));
  const yByDepth = new Map<number, number>([[0, 0]]);
  for (let level = 1; level <= Math.max(...Array.from(depth.values())); level += 1) {
    yByDepth.set(level, yByDepth.get(level - 1)! + rowHeights.get(level - 1)! + ROW_GAP);
  }

  const positions: Record<string, { x: number; y: number }> = {};
  let rightEdge = -Infinity;
  roots.forEach((root, index) => {
    subtreeWidth(root);
    placeLeaves(root, 0);
    let spine = root;
    // Follow the lower median output at each fork to establish the central spine.
    while (children.get(spine)!.length) {
      const branch = children.get(spine)!;
      spine = branch[Math.floor((branch.length - 1) / 2)];
    }
    const anchor = leafCenters.get(spine)!;
    const centers = new Map<string, number>();
    centerOf(root, anchor, centers);
    const ids: string[] = [];
    const collect = (id: string) => { ids.push(id); children.get(id)!.forEach(collect); };
    collect(root);
    const leftEdge = Math.min(...ids.map(id => (centers.get(id) ?? leafCenters.get(id)!) - sizeOf(byId.get(id)!).width / 2));
    const offset = index === 0 ? sizeOf(byId.get(root)!).width / 2 - anchor : rightEdge + COMPONENT_GAP - leftEdge;
    ids.forEach(id => {
      const node = byId.get(id)!;
      const width = sizeOf(node).width;
      const x = Math.round((centers.get(id) ?? leafCenters.get(id)!) - width / 2 + offset);
      positions[id] = { x, y: yByDepth.get(depth.get(id)!)! };
      rightEdge = Math.max(rightEdge, x + width);
    });
  });
  return positions;
};
